import { useEffect, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface DashboardNavSection {
  id: string;
  label: string;
  icon: ReactNode;
  /** The question this section answers; shown on hover as the train of thought. */
  hint: string;
  /** Floats on the chip's top-right corner (e.g. a Beta badge). */
  badge?: ReactNode;
}

/**
 * Sticky scrollspy for the dashboard's sections, ordered the way a brand
 * thinks through their numbers: the whole business at a glance, then the
 * program, then each sales channel. Chips scroll to their section and the
 * one in view stays highlighted.
 */
export function DashboardNav({ sections }: { sections: DashboardNavSection[] }) {
  const [active, setActive] = useState(sections[0]?.id);
  const idsKey = sections.map((s) => s.id).join(",");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      // Top band of the viewport (below the sticky header) decides which
      // section is "current".
      { rootMargin: "-144px 0px -55% 0px" },
    );
    for (const id of idsKey.split(",")) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [idsKey]);

  const go = (id: string) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      aria-label="Dashboard sections"
      className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-card p-1"
    >
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            title={section.hint}
            aria-current={active === section.id ? "true" : undefined}
            onClick={() => go(section.id)}
            className={cn(
              "relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active === section.id
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {section.icon}
            {section.label}
            {section.badge}
          </button>
        ))}
    </nav>
  );
}
